import React, { useEffect, useRef, useState } from "react";

import classnames from "classnames";
import Link from "next/link";
import PropTypes from "prop-types";

import { Button, SwiperCarousel, Text } from "components/ui/source";
import {
  scrollToElementWithOffset,
  scrollToTop,
} from "components/ui/source/util/scrollUtils";

import styles from "./Tab.module.scss";

const carouselProps = {
  loop: false,
  allowTouchMove: true,
  slidesOffsetAfter: 24,
  slidesOffsetBefore: 24,
  spaceBetween: 24,
  breakpoints: {
    1150: {
      allowTouchMove: false,
      slidesOffsetAfter: 80,
      slidesOffsetBefore: 80,
    },
    993: {
      allowTouchMove: true,
      slidesOffsetAfter: 80,
      slidesOffsetBefore: 80,
      spaceBetween: 32,
    },
    577: {
      allowTouchMove: true,
      slidesOffsetAfter: 56,
      slidesOffsetBefore: 56,
    },
  },
};

function Tab({ id, className, button, tabs, isDateSet }) {
  const [selected, setSelected] = useState(tabs[0]?.id || "");
  const observer = useRef(null);

  const [swiper, setSwiper] = useState(null);
  carouselProps.getSwiper = setSwiper;

  useEffect(() => {
    if (!observer.current) {
      observer.current = new window.IntersectionObserver(
        entries => {
          entries.some(entry => {
            // Select tab on detecting the section becomes visible
            if (entry.isIntersecting) {
              setSelected(entry.target.id);
            } else {
              // If no intersection, select prev/next tab section
              // based on current entry's position
              const tabIndex = tabs.findIndex(
                tab => tab.id === entry.target.id,
              );

              if (entry.boundingClientRect.top < 0) {
                setSelected(tabs[tabIndex + 1]?.id || selected);
              } else setSelected(tabs[tabIndex - 1]?.id || selected);
            }
            return true;
          });
        },
        { threshold: [0.2] },
      );
    }

    const { current: currentObserver } = observer;

    tabs.forEach(tab => {
      const anchor = document.getElementById(tab.id);
      if (anchor) {
        currentObserver.observe(anchor);
      }
    });

    return () => currentObserver.disconnect();
  }, [selected, tabs]);

  useEffect(() => {
    // Slide to current tab, only if screen width <= tablet-lg
    if (swiper && swiper.currentBreakpoint !== "1150") {
      const index = tabs.findIndex(tab => tab.id === selected);
      swiper?.slideTo(index);
    }
  }, [swiper, selected, tabs]);

  function onTabClick(event, tabId) {
    event.preventDefault();
    setSelected(tabId);

    // Scroll to selected section
    const tab = tabs.find(tab => tab.id == tabId);
    tab.onClick?.();
    if (tab.scrollToTop) {
      scrollToTop();
    } else {
      scrollToElementWithOffset(tabId, id);
    }
  }

  const tabElements = tabs
    .filter(tab => !tab.hidden)
    .map(tab => (
      <Button
        key={tab.id}
        type="no_background"
        className={classnames(
          styles.tab,
          selected == tab.id && styles.selected,
        )}
        onClick={event => onTabClick(event, tab.id)}
      >
        <Text types={["subtitle1", "medium"]} translateKey={tab.title} />
      </Button>
    ));

  const shortcutButton = (...classNames) => {
    // if href is available render the link button
    if (button?.disableAvailabilitySection) {
      if (button?.href) {
        return (
          <Link passHref href={button.href}>
            <Button
              className={classnames(styles.button, classNames)}
              textProps={{
                types: ["body1"],
                translateKey: button.title,
              }}
              upperCase={false}
            />
          </Link>
        );
      }
      return;
    }

    // Do not show the button if the section is active
    // Need to show the button when date is not set
    if (button == null || (button.id === selected && isDateSet)) return null;
    return (
      <Button
        className={classnames(styles.button, classNames)}
        textProps={{
          types: ["body1"],
          translateKey: button.title,
        }}
        onClick={event => {
          onTabClick(event, button.id);
          button.onClick?.();
        }}
        upperCase={false}
      />
    );
  };

  if (!tabs || tabs.length == 0) return null;

  return (
    <>
      <div id={id} className={classnames(styles.container, className)}>
        <SwiperCarousel
          className={styles.tab_container}
          carouselProps={carouselProps}
          fadingEdge
        >
          {tabElements}
        </SwiperCarousel>
        {shortcutButton(styles.inline_button)}
      </div>
      {shortcutButton(styles.fixed_button, button?.className)}
    </>
  );
}

Tab.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  button: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    className: PropTypes.string,
    onClick: PropTypes.func,
    href: PropTypes.string,
    disableAvailabilitySection: PropTypes.bool,
  }),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      scrollToTop: PropTypes.bool,
      onClick: PropTypes.func,
      hidden: PropTypes.bool,
    }),
  ).isRequired,
};

export default Tab;
